import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { Day, WeeklyPlan } from '../../../fixtures/plan';
import type { WeekDistanceSummary } from '../../../hooks/useWeeklyPlan';
import { daySessionLine, isFutureDay, weekMeasure } from '../../../lib/weekMeasure';
import { DayDetails } from '../../WeekPlan/DayDetails';
import styles from './WeekListV2.module.css';

/**
 * W1-TODAY-WEEK (build spec 7.7, frames 1a, 1d, 1j, 1t, 1v; strings 34 to 49) — the
 * 2.0 week: one card, one measure, seven rows. It is the phone's week list and, with
 * `showDayNumber` on, the desktop week card (`WeekPlanDesktop variant="v2"` renders
 * this). Nothing mounts it until W1-TODAY-WIRE passes the flag.
 *
 * It takes exactly what `WeekPlan` takes: the week the adapter built
 * (`weekPlan` in `TodayHappyPath`, the real `WeeklyPlanWithDistance`) and the same
 * `onToggle(dayId, currentStatus)` (`handleToggleDay`). The week total comes from
 * `weekMeasure`, the one function behind both surfaces: "6 of 40 km" for a distance
 * week, "35 of 235 min" for a time week, nothing for any other week, and "✓ On plan"
 * beside it only on the B330 rule. There is no "behind" text, and a rest day reads
 * "Rest", never a dash placeholder (B364).
 *
 * A row opens that day's panel inline, inside its own list item right after the row's
 * button (`DayDetails variant="v2"`), the only place completion changes. One panel is
 * open at a time. The tapped row holds still: a panel closing above it would pull it
 * up, so the page is offset by that same amount before the frame paints. The page then
 * scrolls only by the shortfall, when the panel's bottom would land under the tab bar
 * and the coach button's clearance (the slot's scroll margin; none on desktop), in
 * 200 ms of the standard ease, or at once under reduced motion. Focus stays on the
 * row's button. Rows are at least 44px tall (`--hit-min`); at a large root the session
 * wraps under the day label and nothing truncates.
 */

/** The glide's length: `--dur-std`. */
const SCROLL_MS = 200;

/** `--ease-std`, cubic-bezier(.2, 0, 0, 1), as a function of progress 0 to 1. */
function easeStd(progress: number): number {
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;
  const [x1, y1, x2, y2] = [0.2, 0, 0, 1];
  const at = (a: number, b: number, u: number) =>
    3 * a * (1 - u) * (1 - u) * u + 3 * b * (1 - u) * u * u + u * u * u;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (at(x1, x2, mid) < progress) lo = mid;
    else hi = mid;
  }
  return at(y1, y2, (lo + hi) / 2);
}

/** Absolute and instant: the glide below sets every frame itself. */
function scrollPageTo(top: number): void {
  window.scrollTo({ top, behavior: 'instant' });
}

export type WeekListV2Props = {
  /** The week `WeekPlan` receives: the adapter output, distance figures optional for fixtures. */
  plan: WeeklyPlan & Partial<WeekDistanceSummary>;
  /**
   * Same contract as `WeekPlan`'s `onToggle`. Rows with a plan-day `id` open their
   * panel when it is set; without it (demo fixtures) the rows are read-only.
   */
  onToggle?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** The desktop card prints the date beside the day ("Mon 12"). Phone: "Mon". */
  showDayNumber?: boolean;
  /**
   * Desktop: web-absolute destination for "Full plan →" (the house `/v2/…` anchor
   * pattern, as `WeekPlanDesktop`). Absent: no link is rendered.
   */
  fullPlanHref?: string;
};

export function WeekListV2({ plan, onToggle, showDayNumber = false, fullPlanHref }: WeekListV2Props) {
  // The selected day's plan_day id. Tapping it again closes the panel.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const panelId = useId();
  const titleId = useId();
  // The open panel's slot (one at a time), the tapped row and where it stood before the
  // tap, and the glide's animation frame.
  const slotRef = useRef<HTMLDivElement>(null);
  const tapRef = useRef<{ row: HTMLElement; top: number } | null>(null);
  const frameRef = useRef(0);
  const measure = weekMeasure(plan);
  const fill =
    measure.kind === 'none' ? 0 : Math.max(0, Math.min(100, Math.round((measure.done / measure.planned) * 100)));

  // After the tap's commit and before paint: hold the tapped row still, then scroll only
  // by the panel's shortfall.
  useLayoutEffect(() => {
    const tap = tapRef.current;
    tapRef.current = null;
    cancelAnimationFrame(frameRef.current);
    if (!tap || !tap.row.isConnected) return;
    const drift = tap.row.getBoundingClientRect().top - tap.top;
    if (drift !== 0) window.scrollBy(0, drift);
    const slot = slotRef.current;
    const panel = slot?.firstElementChild;
    if (!slot || !panel) return;
    const reserve = parseFloat(window.getComputedStyle(slot).scrollMarginBottom) || 0;
    const shortfall = panel.getBoundingClientRect().bottom - (window.innerHeight - reserve);
    // Never carry the tapped row up under the top bar (`--topbar-bottom`, published by
    // TopBar while it is mounted), and never align it there: the shortfall, capped.
    const barBottom =
      parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--topbar-bottom')) || 0;
    const delta = Math.min(shortfall, Math.max(0, tap.row.getBoundingClientRect().top - barBottom));
    if (delta <= 0) return;
    const from = window.scrollY;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scrollPageTo(from + delta);
      return;
    }
    // DayDetails scrolls its own panel into view in this same click's effect flush, to the
    // same line (the panel shares the slot's margin), so the first frame puts the page back
    // at `from` before anything paints and the glide carries it to that line.
    let startedAt: number | null = null;
    const step = (now: number) => {
      startedAt ??= now;
      const progress = Math.min(1, (now - startedAt) / SCROLL_MS);
      scrollPageTo(from + delta * easeStd(progress));
      frameRef.current = progress < 1 ? requestAnimationFrame(step) : 0;
    };
    frameRef.current = requestAnimationFrame(step);
  }, [selectedId]);

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  return (
    <div className={styles.card} role="group" aria-labelledby={titleId}>
      <div className={styles.head}>
        <span id={titleId} className={styles.title}>
          This week
        </span>
        {(measure.kind !== 'none' || fullPlanHref) && (
          <span className={styles.headEnd}>
            {measure.kind === 'distance' && measure.onPlan && (
              <span className={styles.onPlan}>✓ On plan</span>
            )}
            {measure.kind !== 'none' && <span className={`${styles.total} tnum`}>{measure.text}</span>}
            {fullPlanHref && (
              <a className={styles.link} href={fullPlanHref}>
                Full plan →
              </a>
            )}
          </span>
        )}
      </div>
      {measure.kind !== 'none' && (
        <div className={styles.bar} aria-hidden="true">
          <div className={styles.barFill} style={{ width: `${fill}%` }} />
        </div>
      )}
      <ul className={styles.list} role="list">
        {plan.days.map((d) => {
          const dayName = d.dayShort.charAt(0) + d.dayShort.slice(1).toLowerCase();
          const label = showDayNumber ? `${dayName} ${d.dayNumber}` : dayName;
          const session = daySessionLine(d);
          const isDone = d.completionStatus === 'completed';
          const isSkipped = d.completionStatus === 'skipped';
          const chip = isDone ? '✓ Done' : d.today ? 'Today' : null;
          const isClickable = !!onToggle && !!d.id;
          const isSelected = isClickable && d.id === selectedId;
          const className = [
            styles.row,
            d.today ? styles.today : '',
            isSkipped ? styles.skipped : '',
            isClickable ? styles.clickable : '',
            isSelected ? styles.selected : '',
          ]
            .filter(Boolean)
            .join(' ');
          const content = (
            <>
              <span className={`${styles.day} ${showDayNumber ? styles.dayWide : ''}`.trim()}>{label}</span>
              <span className={styles.session}>{session}</span>
              {chip && (
                <span className={isDone ? styles.chipDone : styles.chipToday}>{chip}</span>
              )}
            </>
          );
          // A screen reader SPEAKS this: commas, never a dash.
          const ariaLabel = `${label}, ${session}${isDone ? ', done' : d.today ? ', today' : ''}`;
          return (
            <li key={d.dayShort} className={styles.item}>
              {isClickable ? (
                <button
                  type="button"
                  className={className}
                  onClick={(e) => {
                    tapRef.current = { row: e.currentTarget, top: e.currentTarget.getBoundingClientRect().top };
                    setSelectedId((prev) => (prev === d.id ? null : d.id!));
                  }}
                  aria-label={ariaLabel}
                  aria-expanded={isSelected}
                  aria-controls={isSelected ? panelId : undefined}
                  aria-current={d.today ? 'date' : undefined}
                >
                  {content}
                </button>
              ) : (
                <div className={className} aria-current={d.today ? 'date' : undefined}>
                  {content}
                </div>
              )}
              {isSelected && (
                <div ref={slotRef} className={styles.panelSlot}>
                  <DayDetails
                    variant="v2"
                    day={d}
                    future={isFutureDay(plan.days, d.id)}
                    onToggle={onToggle}
                    id={panelId}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
